<template>
  <div>
    <CRow>
      <!-- Encabezado -->
      <CCol sm="12" md="12" lg="12" xlg="12" class="mb-3">
        <CCard>
          <CCardHeader class="d-flex justify-content-between align-items-center">
            <b><i class="fas fa-file-invoice-dollar"></i>&nbsp;Detalle del Reporte — {{ report.barber_name }}</b>
            <div>
              <CButton v-if="report.state === 0" color="success" size="sm" class="mr-2" :disabled="paying"
                @click="mtd_confirm_pay">
                <span v-if="paying">
                  <i class="fas fa-spinner fa-spin"></i> Procesando...
                </span>
                <span v-else>
                  <i class="fas fa-check"></i> Marcar como pagado
                </span>
              </CButton>
              <CBadge v-else color="success" class="mr-3 p-2">
                <i class="fas fa-check-circle"></i> Pagado el {{ report.fecha_pago }}
              </CBadge>
              <CButton color="secondary" size="sm" @click="mtdBack">
                <i class="fas fa-arrow-left"></i> Volver
              </CButton>
            </div>
          </CCardHeader>
        </CCard>
      </CCol>

      <!-- Columna izquierda: info general + asistencia -->
      <CCol xs="12" md="6">

        <!-- Info general -->
        <CCard class="mb-3">
          <CCardHeader><b><i class="fas fa-info-circle"></i>&nbsp;Información general</b></CCardHeader>
          <CCardBody>
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td class="text-muted">Trabajadora</td>
                  <td><b>{{ report.barber_name }}</b></td>
                </tr>
                <tr>
                  <td class="text-muted">Modalidad</td>
                  <td>
                    <CBadge color="info">{{ report.tipo_pago }}</CBadge>
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Período</td>
                  <td>{{ report.periodo_inicio }} → {{ report.periodo_fin }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Tipo</td>
                  <td>{{ report.tipo_periodo }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Estado</td>
                  <td>
                    <CBadge :color="report.state === 1 ? 'success' : 'warning'">
                      {{ report.state_label }}
                    </CBadge>
                  </td>
                </tr>
              </tbody>
            </table>
          </CCardBody>
        </CCard>

        <!-- Asistencia -->
        <CCard class="mb-3">
          <CCardHeader><b><i class="fas fa-calendar-check"></i>&nbsp;Asistencia</b></CCardHeader>
          <CCardBody>
            <table class="table table-sm mb-0">
              <tbody>
                <tr>
                  <td class="text-muted">Días pagables</td>
                  <td class="text-right text-primary"><b>{{ diasPagables }}</b></td>
                </tr>
                <tr>
                  <td class="text-muted">Días asistidos</td>
                  <td class="text-right text-success"><b>{{ report.dias_asistidos }}</b></td>
                </tr>
                <tr>
                  <td class="text-muted">Faltas injustificadas</td>
                  <td class="text-right text-danger"><b>{{ report.dias_faltados }}</b></td>
                </tr>

                <!-- Tardanza de entrada -->
                <tr class="table-warning">
                  <td class="text-muted">Tardanza entrada</td>
                  <td class="text-right"><b>{{ report.minutos_tardanza }} min</b></td>
                </tr>

                <!-- Salida anticipada -->
                <tr class="table-warning">
                  <td class="text-muted">+ Salida anticipada</td>
                  <td class="text-right"><b>{{ report.minutos_salida_anticipada }} min</b></td>
                </tr>

                <!-- Minutos recuperados: solo se muestra si hay recuperaciones -->
                <template v-if="report.minutos_recuperados > 0">
                  <tr class="table-success">
                    <td class="text-muted">− Minutos recuperados</td>
                    <td class="text-right text-success"><b>{{ report.minutos_recuperados }} min</b></td>
                  </tr>
                </template>

                <!-- Tardanza neta: resultado de la operación -->
                <tr class="table-danger">
                  <td class="text-muted">
                    <b>= Tardanza neta</b>
                    <small class="d-block text-muted" style="font-size:0.75rem">
                      ({{ report.minutos_tardanza }} + {{ report.minutos_salida_anticipada }}
                      <template v-if="report.minutos_recuperados > 0">
                        − {{ report.minutos_recuperados }}
                      </template>)
                    </small>
                  </td>
                  <td class="text-right text-danger"><b>{{ report.minutos_tardanza_netos }} min</b></td>
                </tr>

                <!-- Horas extra -->
                <tr class="table-success">
                  <td class="text-muted">Horas extra</td>
                  <td class="text-right text-success"><b>{{ report.minutos_extra }} min</b></td>
                </tr>
              </tbody>
            </table>
          </CCardBody>
        </CCard>

      </CCol>

      <!-- Columna derecha: montos -->
      <CCol xs="12" md="6">
        <CCard class="mb-3">
          <CCardHeader><b><i class="fas fa-coins"></i>&nbsp;Cálculo de pago</b></CCardHeader>
          <CCardBody>
            <table class="table table-sm mb-0">
              <tbody>

                <!-- Modalidad: base / base_porcentaje -->
                <template v-if="report.tipo_pago === 'base' || report.tipo_pago === 'base_porcentaje'">
                  <tr>
                    <td class="text-muted">Sueldo diario</td>
                    <td class="text-right text-muted">S/ {{ (report.sueldo_base / 30).toFixed(2) }} × 30 días</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Base por asistencia</td>
                    <td class="text-right">S/ {{ report.base_calculado }}</td>
                  </tr>
                  <tr class="table-danger">
                    <td class="text-muted">Descuento tardanza</td>
                    <td class="text-right text-danger">- S/ {{ report.descuento_tardanza }}</td>
                  </tr>
                  <tr class="table-success">
                    <td class="text-muted">Horas extra</td>
                    <td class="text-right text-success">+ S/ {{ report.monto_horas_extra }}</td>
                  </tr>
                </template>

                <!-- Modalidad: porcentaje / base_porcentaje -->
                <template v-if="report.tipo_pago === 'porcentaje' || report.tipo_pago === 'base_porcentaje'">
                  <tr>
                    <td class="text-muted">Monto servicios</td>
                    <td class="text-right">S/ {{ report.monto_servicios }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Comisión ({{ porcentajeLabel }}%)</td>
                    <td class="text-right text-success">+ S/ {{ report.porcentaje_calculado }}</td>
                  </tr>
                  <template v-if="report.tipo_pago === 'porcentaje'">
                    <tr class="table-danger">
                      <td class="text-muted">Descuento tardanza</td>
                      <td class="text-right text-danger">- S/ {{ report.descuento_tardanza }}</td>
                    </tr>
                    <tr class="table-success">
                      <td class="text-muted">Horas extra</td>
                      <td class="text-right text-success">+ S/ {{ report.monto_horas_extra }}</td>
                    </tr>
                  </template>
                </template>

                <!-- Modalidad: meta -->
                <template v-if="report.tipo_pago === 'meta'">
                  <tr>
                    <td class="text-muted">Meta mínima</td>
                    <td class="text-right text-info"><b>S/ {{ metaMinima }}</b></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Monto servicios</td>
                    <td class="text-right">S/ {{ report.monto_servicios }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Por servicios ({{ porcentajeLabel }}%)</td>
                    <td class="text-right text-success">+ S/ {{ report.porcentaje_calculado }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Complemento empresa</td>
                    <td class="text-right text-success">+ S/ {{ report.complemento_empresa }}</td>
                  </tr>
                  <tr class="table-danger">
                    <td class="text-muted">Descuento tardanza</td>
                    <td class="text-right text-danger">- S/ {{ report.descuento_tardanza }}</td>
                  </tr>
                  <tr class="table-success">
                    <td class="text-muted">Horas extra</td>
                    <td class="text-right text-success">+ S/ {{ report.monto_horas_extra }}</td>
                  </tr>
                </template>

                <tr class="table-warning">
                  <td class="text-muted">Total descuentos</td>
                  <td class="text-right text-danger"><b>- S/ {{ report.total_descuentos }}</b></td>
                </tr>
                <tr class="table-success">
                  <td><b>TOTAL A PAGAR</b></td>
                  <td class="text-right">
                    <b class="text-success" style="font-size:1.1rem">S/ {{ report.total_pagar }}</b>
                  </td>
                </tr>

              </tbody>
            </table>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Modal: confirmar fecha de pago -->
    <CModal :show.sync="modal_pay.show" title="Confirmar pago" color="success" size="sm">
      <CRow>
        <CCol sm="12" md="12" lg="12">
          <p>
            ¿Confirmas el pago de <b>S/ {{ report.total_pagar }}</b>
            a <b>{{ report.barber_name }}</b>?
          </p>
        </CCol>
        <CCol sm="12" md="12" lg="12">
          <CInput label="Fecha de pago" type="date" size="sm" v-model="modal_pay.fecha_pago" />
        </CCol>
      </CRow>

      <template #footer>
        <CButton color="secondary" size="sm" @click="modal_pay.show = false">
          Cancelar
        </CButton>
        <CButton color="success" size="sm" :disabled="paying" @click="mtd_pay">
          <i class="fas fa-check"></i> Confirmar pago
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";

export default {
  name: "detalle-pago",

  props: {
    reportData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      prefix: "paymentReport",
      paying: false,
      report: { ...this.reportData },

      modal_pay: {
        show: false,
        fecha_pago: moment().format("YYYY-MM-DD"),
      },
    };
  },

  computed: {
    porcentajeLabel() {
      if (!this.report.monto_servicios || this.report.monto_servicios == 0) return "—";
      return ((this.report.porcentaje_calculado / this.report.monto_servicios) * 100).toFixed(0);
    },
    metaMinima() {
      const monto = parseFloat(this.report.meta_monto) || 0;
      return this.report.tipo_periodo === 'quincenal'
        ? (monto / 2).toFixed(2)
        : monto.toFixed(2);
    },
    diasPagables() {
      const totalDias = this.daysBetween(this.report.periodo_inicio, this.report.periodo_fin);
      const descanso = Math.max(0, totalDias - this.report.dias_laborables);
      return this.report.dias_asistidos + descanso;
    },
  },

  created() {
    if (this.reportData.Id && !this.reportData.dias_laborables) {
      this.mtd_fetch();
    }
  },

  methods: {
    ...mapActions(["get", "post"]),

    mtdBack() {
      this.$emit("mtdBack");
    },

    mtd_fetch() {
      this.get({
        url: `${this.$store.getters.get__url}/${this.prefix}/${this.reportData.Id}/show`,
        token: this.$store.getters.get__token,
      }).then((response) => {
        if (response.statusCode == 200) {
          this.report = response.data;
        }
      }).catch(() => {
        Swal.fire({ title: "Error al cargar el detalle", icon: "error" });
      });
    },

    mtd_confirm_pay() {
      this.modal_pay = {
        show: true,
        fecha_pago: moment().format("YYYY-MM-DD"),
      };
    },

    mtd_pay() {
      if (!this.modal_pay.fecha_pago) {
        Swal.fire({ title: "Ingresa la fecha de pago", icon: "warning" });
        return;
      }

      this.paying = true;

      this.post({
        url: `${this.$store.getters.get__url}/${this.prefix}/markAsPaid`,
        token: this.$store.getters.get__token,
        params: {
          id: this.report.Id,
          fecha_pago: this.modal_pay.fecha_pago,
        },
      }).then((response) => {
        this.paying = false;
        if (response.statusCode == 200) {
          this.modal_pay.show = false;
          this.report.state = 1;
          this.report.state_label = "Pagado";
          this.report.fecha_pago = response.data.fecha_pago;
          Swal.fire({ title: "Pago registrado correctamente", icon: "success", timer: 1500 });
        } else {
          Swal.fire({ title: response.message ?? "Error al registrar el pago", icon: "error" });
        }
      }).catch(() => {
        this.paying = false;
        Swal.fire({ title: "Error al registrar el pago", icon: "error" });
      });
    },
    daysBetween(inicio, fin) {
      const a = new Date(inicio);
      const b = new Date(fin);
      return Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
    },
  },
};
</script>
<style lang="scss" scoped></style>